import React, {useCallback, useEffect, useState} from "react";
import {Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Switch,
        MenuItem, TextField, Tooltip, Typography} from "@mui/material";
import {AddOutlined, CloseOutlined, DashboardOutlined, DeleteOutlined, Home as HomeFilled,
        HomeOutlined, RefreshOutlined} from "@mui/icons-material";
import {useNavigate, useParams} from "react-router";

import PageHeader from "../components/Common/PageHeader";
import PersonaHeaderInfo from "../components/Common/PersonaHeaderInfo";
import PersonaScope from "../components/Reporting/PersonaScope";
import DashboardTile from "../components/Reporting/DashboardTile";
import DashboardTableApplet from "../eoec/applet/impl/Reporting/DashboardTableApplet";
import ChartBuilder from "../components/Reporting/ChartBuilder";
import ReportChart from "../components/Reporting/ReportChart";
import {ChartSpec, TileDrawing, drawingOf} from "../components/Reporting/chartSpec";
import css_buttons from "../components/Common/css/CCButton.module.scss";
import EventBus from "../util/EventBus";
import {Blue3, Grey6, Grey17} from "../theme/styles";
// NO getDashboards AND NO DashboardSummary any more: the grid's EnterpriseComponent fetches the
// list itself, which is the point of the conversion. Left imported they are a CRA build ERROR
// rather than a warning, because CI=true promotes unused imports.
import {Dashboard, Tile, TileResult, deleteDashboard, getDashboard,
        runDashboard, saveDashboard, setHomeDashboard, chartForTile, setTileChart}
    from "../components/Reporting/dashboardApi";
import {SavedReport, getSavedReports} from "../components/Reporting/reportStoreApi";
import {makeEnterpriseComponent} from "../util/utility";

/**
 * Dashboards: a page of saved reports, drawn.
 *
 * ONE SCREEN, TWO STATES, because a dashboard is small enough that a separate editor would be two
 * pages showing the same thing. Without a USID this lists them; with one it shows that dashboard,
 * and Edit turns the same page into its own editor.
 *
 * EVERY TILE RUNS UNDER THE VIEWER'S PERSONA, which is the whole reason a dashboard is safe to
 * share: two people open the same one and each sees their own rows. So the persona tabs are here,
 * exactly as they are on the builder, and switching one re-runs the page — the numbers are a
 * property of who is looking, and leaving stale ones on screen after a switch would be the single
 * most misleading thing this feature could do.
 *
 * ONE REQUEST FOR THE WHOLE PAGE. `runDashboard` returns every tile together, and the reason is
 * COHERENCE rather than capacity: each tile fetching itself fails as "some tiles drew and some did
 * not", which reads as a data problem and sends somebody looking at the data. An earlier version of
 * this comment justified it with a prod Lambda concurrency cap of 10; the cap is 1000, checked in
 * Service Quotas, and the argument never needed it.
 */

const EMPTY_TILE = (reportUSID: string): Tile =>
    ({ReportUSID: reportUSID, ChartKind: "table", Width: 1, Title: null});

export default function Dashboards() {
    const {usid} = useParams();
    const navigate = useNavigate();

    // THE LIST IS THE GRID'S NOW, and it fetches its own rows -- so the page holds the component
    // rather than an array. Created ONCE: a component rebuilt per render is a grid REMOUNTED per
    // render, which throws away the reader's column widths, sort and search on every keystroke
    // elsewhere on the page. The same lesson as the result grid in ReportBuilder.
    const [listComponent] = useState<any>(() => makeEnterpriseComponent("Dashboard"));
    const [dashboard, setDashboard] = useState<Dashboard | null>(null);
    const [tiles, setTiles] = useState<TileResult[]>([]);
    const [reports, setReports] = useState<SavedReport[]>([]);
    const [arranging, setArranging] = useState(false);
    const [draft, setDraft] = useState<
        {Name: string; Description: string; Tiles: Tile[]; IsHome: boolean;
         IsPrivate: boolean} | null>(null);
    const [busy, setBusy] = useState(false);
    const [failure, setFailure] = useState<string | null>(null);
    // THE TILE WHOSE CHART IS BEING CHANGED. Editing happens HERE, on the dashboard, and not in the
    // arranger -- the arranger knows which reports appear and in what order, and has no rows to
    // draw. A tile on screen has its own columns and rows already, which is exactly what a chart
    // builder needs.
    const [drawing, setDrawing] = useState<TileResult | null>(null);

    // REFRESHING THE LIST IS ASKING THE GRID TO FETCH AGAIN, not replacing an array the page owns.
    // Saving or deleting a dashboard changes what the list should say, and without this the grid
    // goes on showing the answer from before the change -- which looks like the save failing.
    const loadList = useCallback(() => {
        listComponent?.newQuery?.();
        listComponent?.invalidate?.();
        // AND THE RAIL, which names these dashboards under Home from a different tree. Announced
        // here rather than at each call site: every place that changes the list already refreshes
        // it, and the rail needs to hear about exactly the same changes.
        EventBus.dashboards.emit("change-dashboards");
    }, [listComponent]);

    const loadOne = useCallback((id: string) => {
        setFailure(null);
        setBusy(true);
        getDashboard(id)
            .then(one => {
                setDashboard(one);
                // THE ARRANGEMENT FIRST, THE NUMBERS AFTER. The tiles render with their headings
                // immediately and fill in as the run returns, rather than the page staying blank.
                setTiles((one?.Tiles ?? []).map(tile => ({...tile})));
                return runDashboard(id).then(setTiles);
            })
            .catch((error: any) => setFailure(
                error?.response?.data?.Message ?? "That dashboard could not be opened."))
            .finally(() => setBusy(false));
    }, []);

    useEffect(() => { loadList(); getSavedReports().then(setReports).catch(() => setReports([])); },
              [loadList]);

    useEffect(() => {
        if (usid) loadOne(usid);
        else { setDashboard(null); setTiles([]); setArranging(false); setDraft(null); }
    }, [usid, loadOne]);

    // SWITCHING PERSONA RE-RUNS THE PAGE. The numbers belong to whoever is looking; leaving the
    // previous persona's totals on screen under the new one's name is the most misleading thing
    // this feature could do.
    //
    // AND THE LIST, NOT ONLY THE OPEN DASHBOARD. This re-ran `loadOne` and nothing else, so the
    // half of the page that reacted was the half you could only reach by having already opened
    // something: standing on the list and changing persona left the previous persona's rows on
    // screen under the new persona's name, with the header beside them saying otherwise. The list
    // is fetched with the persona's own headers like everything else, so it has the same claim to
    // being re-asked -- and a dashboard somebody else keeps private is in exactly that answer.
    useEffect(() => {
        const onChange = () => { loadList(); if (usid) loadOne(usid); };
        EventBus.visibility.addListener("change-visibility", onChange);
        return () => { EventBus.visibility.removeListener("change-visibility", onChange); };
    }, [usid, loadOne, loadList]);

    const startArranging = () => {
        setDraft({Name: dashboard?.Name ?? "", Description: dashboard?.Description ?? "",
                  IsHome: !!dashboard?.IsHome, IsPrivate: !!dashboard?.IsPrivate,
                  Tiles: (dashboard?.Tiles ?? []).map(one => ({...one}))});
        setArranging(true);
    };

    const keep = () => {
        if (!draft) return;
        setBusy(true);
        setFailure(null);
        saveDashboard({USID: dashboard?.USID, Name: draft.Name, Description: draft.Description,
                       Tiles: draft.Tiles, IsHome: draft.IsHome,
                       IsPrivate: draft.IsPrivate})
            .then((answer: any) => {
                setArranging(false);
                setDraft(null);
                loadList();
                if (answer?.USID && answer.USID !== usid) navigate(`/dashboards/${answer.USID}`);
                else if (usid) loadOne(usid);
            })
            .catch((error: any) => setFailure(
                error?.response?.data?.Message ?? "That dashboard could not be saved."))
            .finally(() => setBusy(false));
    };

    const remove = () => {
        if (!dashboard) return;
        setBusy(true);
        deleteDashboard(dashboard.USID)
            .then(() => { loadList(); navigate("/dashboards"); })
            .catch((error: any) => setFailure(
                error?.response?.data?.Message ?? "That dashboard could not be removed."))
            .finally(() => setBusy(false));
    };

    // THE SAME ACTION BOTH WAYS. Passing null clears the preference, which is not "no Home page"
    // but "whatever the account chose" -- so this toggles rather than needing a second control to
    // undo it.
    const chooseAsHome = () => {
        if (!dashboard) return;
        setBusy(true);
        setFailure(null);
        setHomeDashboard(dashboard.MyHome ? null : dashboard.USID)
            .then(() => { if (usid) loadOne(usid); })
            .catch((error: any) => setFailure(
                error?.response?.data?.Message ?? "That Home page could not be set."))
            .finally(() => setBusy(false));
    };

    const moveTile = (index: number, by: -1 | 1) => {
        if (!draft) return;
        const to = index + by;
        if (to < 0 || to >= draft.Tiles.length) return;
        const next = [...draft.Tiles];
        [next[index], next[to]] = [next[to], next[index]];
        setDraft({...draft, Tiles: next});
    };

    const setTile = (index: number, over: Partial<Tile>) => {
        if (!draft) return;
        setDraft({...draft, Tiles: draft.Tiles.map((one, at) => at === index ? {...one, ...over} : one)});
    };

    return (
        <div style={{display: "flex", flexDirection: "column", flexGrow: 1, minWidth: 0,
                     height: "100%", backgroundColor: "#FFFFFF"}}>
            <PageHeader title="Reporting">
                <PersonaHeaderInfo/>
            </PageHeader>

            <div style={{padding: "20px 24px", overflowY: "auto"}}>
                <div style={{display: "flex", alignItems: "center", gap: 8, marginBottom: 14}}>
                    <DashboardOutlined sx={{color: Blue3, fontSize: 20}}/>
                    <Typography style={{fontSize: 20, fontWeight: 500, color: Grey17}}>
                        {dashboard ? dashboard.Name : "Dashboards"}
                    </Typography>
                    <div style={{flexGrow: 1}}/>
                    {dashboard && !arranging ? (
                        <>
                            <Tooltip title="Run every tile again">
                                <IconButton size="small" aria-label="Refresh this dashboard"
                                            onClick={() => usid && loadOne(usid)}>
                                    <RefreshOutlined sx={{fontSize: 18, color: Grey6}}/>
                                </IconButton>
                            </Tooltip>
                            {/* MY HOME PAGE, CHOSEN HERE. It is a preference and not a
                                navigation -- "this is what I want to see when I arrive" -- so it
                                belongs on the dashboard it is about, where the reader can see what
                                they are choosing. Putting it in the rail would have meant a click
                                that navigates AND silently changes where you land tomorrow.

                                IT SAYS WHICH WAY IT WILL GO, from `MyHome`. That cannot be derived
                                from `IsHome`: the account's Home page is one row for the realm and
                                a preference is one row per person, so a dashboard can be the
                                account's and not mine, or mine and not the account's. */}
                            <Tooltip title={dashboard.MyHome
                                            ? "Stop using this as my Home page"
                                            : "Show this dashboard on my Home page"}>
                                <IconButton size="small" onClick={chooseAsHome}
                                            aria-label={dashboard.MyHome
                                                        ? "Stop using this as my Home page"
                                                        : "Make this my Home page"}>
                                    {/* FILLED WHEN IT IS MINE. The same glyph both ways, because
                                        it is one thing being on or off -- a second glyph would
                                        read as a second control. */}
                                    {dashboard.MyHome
                                        ? <HomeFilled sx={{fontSize: 18, color: Blue3}}/>
                                        : <HomeOutlined sx={{fontSize: 18, color: Grey6}}/>}
                                </IconButton>
                            </Tooltip>
                            <Button size="small" className={css_buttons.blueStandardButton}
                                    onClick={startArranging}>Edit</Button>
                        </>
                    ) : null}
                </div>

                {/* THE SWITCHER BELONGS TO A DASHBOARD, NOT TO THE LIST. On the list there is
                    nothing whose numbers it changes -- the list is realm-wide -- so pressing it
                    re-rendered the tabs and altered nothing on screen, while quietly setting the
                    session's visibility for whatever was opened next. A control with no feedback
                    where it was pressed and an effect somewhere else is worse than no control.

                    THE READOUT STAYS ON BOTH, in the title bar above: the persona is what the
                    numbers MEAN, and naming it is not the same job as changing it -- which is the
                    separation Terms and Rules already make. */}
                {usid ? (
                    <div style={{display: "flex", flexDirection: "row",
                                 justifyContent: "space-between", alignItems: "flex-end",
                                 borderBottom: "1px solid #E4E7EC", marginBottom: 20}}>
                        <PersonaScope/>
                    </div>
                ) : null}

                {failure ? (
                    <Typography sx={{fontSize: 13, color: "#B42318", mb: 2}}>{failure}</Typography>
                ) : null}

                {/* NOT WHILE ARRANGING. Creating a dashboard used to leave the whole list sitting
                    above the editor -- tolerable when the list was a short stack of boxes, and not
                    once it became a grid with a header, a footer and paging controls between the
                    reader and the fields they are filling in. Editing an EXISTING one already hid
                    it, because that happens on the dashboard's own route; this makes the two
                    consistent rather than differing by which way you got here. */}
                {!usid && !arranging ? (
                    <DashboardList component={listComponent} reports={reports}
                                   onNew={() => { setDashboard(null);
                                                  setDraft({Name: "", Description: "", Tiles: [],
                                                            IsHome: false, IsPrivate: false});
                                                  setArranging(true); }}/>
                ) : null}

                {drawing ? (
                    <TileChartDialog tile={drawing}
                                     onClose={() => setDrawing(null)}
                                     onSaved={() => { setDrawing(null); if (usid) loadOne(usid); }}
                                     onFailure={setFailure}/>
                ) : null}

                {arranging && draft ? (
                    <Arranger draft={draft} reports={reports} busy={busy}
                              onChange={setDraft} onMove={moveTile} onTile={setTile}
                              onCancel={() => { setArranging(false); setDraft(null); }}
                              onSave={keep} onDelete={dashboard ? remove : undefined}/>
                ) : null}

                {usid && !arranging ? (
                    tiles.length === 0 ? (
                        <Typography sx={{fontSize: 12.5, color: Grey6}}>
                            {busy ? "Opening…" : "This dashboard has no tiles yet. Press Edit to add one."}
                        </Typography>
                    ) : (
                        <Box sx={{display: "flex", flexWrap: "wrap", gap: "12px"}}>
                            {tiles.map(tile => (
                                <DashboardTile key={tile.USID ?? tile.ReportUSID} tile={tile}
                                               loading={busy && !tile.Columns && !tile.Refusal}
                                               onChangeChart={setDrawing}/>
                            ))}
                        </Box>
                    )
                ) : null}
            </div>
        </div>
    );
}

function DashboardList({component, reports, onNew}: {
    component: any; reports: SavedReport[]; onNew: () => void;
}) {
    return (
        <Box>
            {/* THE ACTION SITS AT THE FAR RIGHT, which is the standard across the suite: the row
                reads left to right as "what this is, then what you can do about it", and a primary
                action ranged left collides with the table's own title beneath it. The spacer is a
                flexGrow div rather than justifyContent, because the reason-why message has to stay
                beside the button rather than at the other end of the row from it. */}
            <Box sx={{display: "flex", alignItems: "center", gap: 2, mb: 2}}>
                <div style={{flexGrow: 1}}/>
                {reports.length === 0 ? (
                    // THE REASON, NOT JUST A DISABLED BUTTON. A dashboard is made OF saved reports,
                    // so with none there is nothing to put on one — and that is a thing to go and
                    // do, not a fault.
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>
                        Save a report first — a dashboard is built from reports you have kept.
                    </Typography>
                ) : null}
                <Button size="small" className={css_buttons.blueStandardButton}
                        disabled={reports.length === 0} onClick={onNew}>New dashboard</Button>
            </Box>

            {/* NO "No dashboards yet." BESIDE IT ANY MORE. PlatformTable renders its own empty
                state and its own footer count, and a second sentence saying the same thing was one
                of the things the hand-rolled list was doing that the grid already does. */}
            <DashboardTableApplet enterpriseComponent={component}/>
        </Box>
    );
}

function Arranger({draft, reports, busy, onChange, onMove, onTile, onCancel, onSave, onDelete}: {
    draft: {Name: string; Description: string; Tiles: Tile[]; IsHome: boolean;
            IsPrivate: boolean};
    reports: SavedReport[];
    busy: boolean;
    onChange: (draft: {Name: string; Description: string; Tiles: Tile[]; IsHome: boolean;
                       IsPrivate: boolean}) => void;
    onMove: (index: number, by: -1 | 1) => void;
    onTile: (index: number, over: Partial<Tile>) => void;
    onCancel: () => void;
    onSave: () => void;
    onDelete?: () => void;
}) {
    const addTile = () => {
        const first = reports[0];
        if (!first) return;
        onChange({...draft, Tiles: [...draft.Tiles, EMPTY_TILE(first.USID)]});
    };

    return (
        <Box sx={{maxWidth: 900}}>
            <Box sx={{display: "flex", gap: 2, mb: 2, flexWrap: "wrap"}}>
                <TextField size="small" label="Name" sx={{width: 320}} value={draft.Name}
                           onChange={event => onChange({...draft, Name: event.target.value})}/>
                <TextField size="small" label="Description" sx={{width: 420}}
                           value={draft.Description}
                           onChange={event => onChange({...draft, Description: event.target.value})}/>

                {/* ONE PER ACCOUNT, AND IT IS THE ACCOUNT'S rather than yours -- so the wording
                    says so. Ticking it here takes the home page from whichever dashboard had it,
                    which is the reach dashboards already have: the list is realm-wide and a tile
                    runs under whoever is looking. A home page each would be a user preference and
                    would not live in this dialog. */}
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    <Switch size="small" checked={draft.IsHome} disabled={draft.IsPrivate}
                            slotProps={{input: {"aria-label": "Show this on the Home page"}}}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                onChange({...draft, IsHome: event.target.checked})}/>
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>
                        Show on the Home page
                    </Typography>
                </Box>

                {/* PRIVATE MEANS ONLY YOU CAN SEE IT -- not "hidden from the list". Somebody else
                    with the link cannot open, run or change it either; the two would be a
                    difference worth nobody discovering the hard way.

                    THE TWO SWITCHES ARE EXCLUSIVE and the screen says so rather than letting the
                    server refuse after a save: the home page is what the whole account lands on,
                    so a private one would be a page most of them cannot open. */}
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                    <Switch size="small" checked={draft.IsPrivate}
                            slotProps={{input: {"aria-label": "Private to me"}}}
                            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                onChange({...draft, IsPrivate: event.target.checked,
                                          IsHome: event.target.checked ? false : draft.IsHome})}/>
                    <Typography sx={{fontSize: 12.5, color: Grey6}}>
                        Private to me
                    </Typography>
                </Box>
            </Box>

            <Typography sx={{fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
                             textTransform: "uppercase", color: Blue3, mb: 0.5}}>
                Tiles
            </Typography>
            <Typography sx={{fontSize: 12.5, color: Grey6, mb: 1.5}}>
                Each tile is a report you have saved, drawn how you choose. They run when the
                dashboard is opened, under the persona of whoever opens it.
            </Typography>

            {draft.Tiles.map((tile, index) => (
                <Box key={index} sx={{display: "flex", alignItems: "center", gap: 1, mb: 1,
                                      flexWrap: "wrap"}}>
                    {/* ARROWS, for the reason the shelves use them: dragging is pointer-only and
                        these are ordinary buttons with ordinary names. */}
                    <Box sx={{display: "flex", flexDirection: "column"}}>
                        <IconButton size="small" disabled={index === 0} sx={{p: 0, width: 16, height: 16}}
                                    aria-label={`Move tile ${index + 1} up`}
                                    onClick={() => onMove(index, -1)}>
                            <Typography sx={{fontSize: 11, color: Grey6}}>▲</Typography>
                        </IconButton>
                        <IconButton size="small" disabled={index === draft.Tiles.length - 1}
                                    sx={{p: 0, width: 16, height: 16}}
                                    aria-label={`Move tile ${index + 1} down`}
                                    onClick={() => onMove(index, 1)}>
                            <Typography sx={{fontSize: 11, color: Grey6}}>▼</Typography>
                        </IconButton>
                    </Box>

                    <TextField select size="small" sx={{width: 260}} label="Report"
                               value={tile.ReportUSID}
                               onChange={event => onTile(index, {ReportUSID: event.target.value})}>
                        {reports.map(one => (
                            <MenuItem key={one.USID} value={one.USID}>{one.Name}</MenuItem>
                        ))}
                    </TextField>

                    {/* TWO CHOICES, NOT A LIST OF CHART TYPES. The type comes from the chart
                        built on the report -- or from this tile's own override, edited on the
                        dashboard itself -- so offering "Bar" here could only ever agree with what
                        is drawn by coincidence. The value is read through `drawingOf` so a tile
                        saved under an old chart name shows as "Chart" without being rewritten:
                        selecting the entry it is already on fires nothing, and the stored name
                        survives to keep being that tile's fallback. */}
                    <TextField select size="small" sx={{width: 130}} label="Draw as"
                               value={drawingOf(tile.ChartKind)}
                               onChange={event => onTile(index,
                                   {ChartKind: event.target.value as TileDrawing})}>
                        <MenuItem value="table">Table</MenuItem>
                        <MenuItem value="chart">Chart</MenuItem>
                    </TextField>

                    <TextField select size="small" sx={{width: 110}} label="Width"
                               value={String(tile.Width)}
                               onChange={event => onTile(index, {Width: Number(event.target.value) as 1 | 2})}>
                        <MenuItem value="1">Half</MenuItem>
                        <MenuItem value="2">Full</MenuItem>
                    </TextField>

                    <TextField size="small" sx={{width: 200}} label="Heading (optional)"
                               value={tile.Title ?? ""}
                               onChange={event => onTile(index, {Title: event.target.value})}/>

                    <IconButton size="small" aria-label={`Remove tile ${index + 1}`}
                                onClick={() => onChange({...draft,
                                    Tiles: draft.Tiles.filter((_one, at) => at !== index)})}>
                        <CloseOutlined sx={{fontSize: 16, color: Grey6}}/>
                    </IconButton>
                </Box>
            ))}

            <Box sx={{display: "flex", alignItems: "center", gap: 2, mt: 2}}>
                <Button size="small" startIcon={<AddOutlined/>} onClick={addTile}
                        sx={{textTransform: "none"}}>Add a tile</Button>
                <div style={{flexGrow: 1}}/>
                {onDelete ? (
                    <Button size="small" startIcon={<DeleteOutlined/>} onClick={onDelete}
                            sx={{textTransform: "none", color: "#B42318"}}>Delete</Button>
                ) : null}
                <Button size="small" onClick={onCancel} sx={{textTransform: "none"}}>Cancel</Button>
                <Button size="small" className={css_buttons.blueStandardButton}
                        disabled={busy || !draft.Name.trim()} onClick={onSave}>
                    {busy ? "Saving…" : "Save"}
                </Button>
            </Box>
        </Box>
    );
}

/**
 * Changing how ONE tile is drawn.
 *
 * ON THE DASHBOARD, NOT IN THE ARRANGER. The arranger decides which reports appear and in what
 * order; it has no rows, and a chart builder without rows can offer nothing and refuse nothing. A
 * tile on screen has already run — its columns and rows are in hand — so this is the one place the
 * builder can show a live preview and grey out a chart with a reason.
 *
 * SAVES ONE TILE. Not the dashboard: a page that is only showing tiles must not resend the
 * arrangement to change a colour, because a stale tile list is how somebody else's reorder is
 * quietly undone.
 */
function TileChartDialog({tile, onClose, onSaved, onFailure}: {
    tile: TileResult;
    onClose: () => void;
    onSaved: () => void;
    onFailure: (why: string) => void;
}) {
    const columns = tile.Columns ?? [];
    const rows = tile.Rows ?? [];
    const [spec, setSpec] = useState<ChartSpec>(() => chartForTile(tile, columns));
    const [saving, setSaving] = useState(false);

    const following = !tile.TileChartSpecification;

    const write = (next: ChartSpec | null) => {
        setSaving(true);
        setTileChart(String(tile.USID), next)
            .then(onSaved)
            .catch(() => onFailure("That tile's chart could not be saved."))
            .finally(() => setSaving(false));
    };

    return (
        <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{fontSize: 17}}>
                How {tile.Title || tile.ReportName || "this tile"} is drawn
            </DialogTitle>
            <DialogContent>
                <Typography sx={{fontSize: 12.5, color: Grey6, mb: 2, maxWidth: "80ch"}}>
                    {following
                        ? "This tile follows its report. Change anything here and it keeps its own "
                          + "chart instead, and stops changing when the report's chart does."
                        : "This tile has its own chart. Use the report's chart to put it back in "
                          + "step with everywhere else that report appears."}
                </Typography>

                <ChartBuilder spec={spec} columns={columns} rows={rows} onChange={setSpec}/>
                <ReportChart spec={spec} columns={columns} rows={rows} height={300}/>
            </DialogContent>
            <DialogActions>
                {!following ? (
                    <Button onClick={() => write(null)} disabled={saving}
                            sx={{textTransform: "none", color: "#B42318", mr: "auto"}}>
                        Use the report's chart
                    </Button>
                ) : null}
                <Button onClick={onClose} sx={{textTransform: "none"}}>Cancel</Button>
                <Button onClick={() => write(spec)} disabled={saving}
                        className={css_buttons.blueStandardButton} size="small">
                    {saving ? "Saving…" : "Use this for the tile"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
